import { ChevronLeft, ChevronRight, Maximize2, Minimize2 } from "lucide-react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type CSSProperties } from "react";

import { TvFrame, type TvFrameHandle, type TvFrameLayout, type TvFramePageInfo } from "./TvFrame";
import { PREVIEW_STAGE_BG, PreviewThemeToggle, type PreviewTheme } from "./TvFrameTheme";
import { isHeaderPage, navigationHeaderPage } from "../runtime/pageNavigation";

/* The published-report stage shared by the share link page and the in-app
 * viewer, so both draw a version exactly like the studio's published preview:
 *  - the page's OWN canvas, uniformly scaled (`fit=fixed`), never stretched;
 *  - the frame follows the page size (A4 portrait/landscape, any canvas) -
 *    `adaptive` - and is never assumed to be 16:9 1920x1080;
 *  - colour mode comes from the caller (URL / report default), not from a
 *    per-browser setting;
 *  - "Fit page" (whole page visible) or "Fit width" (fills the width, scrolls);
 *  - real full screen (Fullscreen API; ESC exits) showing only the report,
 *    with a toolbar that appears on pointer movement. */

export type ViewerFitMode = "page" | "width";

export interface TvFrameViewerHandle {
  toggleFullscreen: () => void;
  readonly frame: TvFrameHandle | null;
}

interface Props {
  reportId: string;
  source: "published" | "share";
  shareToken?: string;
  theme: PreviewTheme;
  onThemeChange: (t: PreviewTheme) => void;
  fitMode: ViewerFitMode;
  onFitMode: (m: ViewerFitMode) => void;
  pages: TvFramePageInfo[];
  pageId: string | null;
  onLoaded: (pages: TvFramePageInfo[], pageId: string) => void;
  onPage: (pageId: string) => void;
  /** Pixels of page chrome above the stage (header rows): the "fit page" height is 100vh minus this. */
  reserve: number;
  reloadKey?: number;
  onFullscreenChange?: (full: boolean) => void;
}

export function FitModeToggle({ value, onChange }: { value: ViewerFitMode; onChange: (m: ViewerFitMode) => void }) {
  return (
    <div className="st-tvt" role="group" aria-label="Page fit" title="Fit page shows the whole page; Fit width fills the width and scrolls.">
      <button type="button" className="st-tvt__btn" aria-pressed={value === "page"} onClick={() => onChange("page")}>Fit page</button>
      <button type="button" className="st-tvt__btn" aria-pressed={value === "width"} onClick={() => onChange("width")}>Fit width</button>
    </div>
  );
}

export const TvFrameViewer = forwardRef<TvFrameViewerHandle, Props>(function TvFrameViewer(
  { reportId, source, shareToken, theme, onThemeChange, fitMode, onFitMode, pages, pageId, onLoaded, onPage, reserve, reloadKey, onFullscreenChange },
  ref,
) {
  const wrap = useRef<HTMLDivElement>(null);
  const frame = useRef<TvFrameHandle>(null);
  const [full, setFull] = useState(false);
  const [aspect, setAspect] = useState(16 / 9);
  const [barVisible, setBarVisible] = useState(false);
  const hideTimer = useRef<number | undefined>(undefined);

  const setFullState = useCallback((v: boolean) => {
    setFull(v);
    onFullscreenChange?.(v);
  }, [onFullscreenChange]);

  // Real Fullscreen API; where it is missing (some phones) fall back to a fixed overlay + ESC.
  const [pseudo, setPseudo] = useState(false);
  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
      return;
    }
    if (pseudo) {
      setPseudo(false);
      setFullState(false);
      return;
    }
    const el = wrap.current;
    if (el && el.requestFullscreen) {
      el.requestFullscreen().catch(() => { setPseudo(true); setFullState(true); });
    } else {
      setPseudo(true);
      setFullState(true);
    }
  }, [pseudo, setFullState]);

  useImperativeHandle(ref, () => ({ toggleFullscreen, get frame() { return frame.current; } }), [toggleFullscreen]);

  useEffect(() => {
    const h = () => setFullState(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, [setFullState]);

  useEffect(() => {
    if (!pseudo) return;
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") { setPseudo(false); setFullState(false); } };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [pseudo, setFullState]);

  const reveal = useCallback(() => {
    setBarVisible(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setBarVisible(false), 2600);
  }, []);
  useEffect(() => () => window.clearTimeout(hideTimer.current), []);

  // previous / next page (header pages only, like the page picker)
  const navPages = pages.filter((p) => isHeaderPage(pages, p));
  const current = navigationHeaderPage(pages, pageId ?? "");
  const idx = navPages.findIndex((p) => p.id === current);
  const go = (to: number) => {
    const p = navPages[to];
    if (!p) return;
    frame.current?.post({ type: "mrp:selectPage", pageId: p.id });
    onPage(p.id);
  };

  // frame width: page mode = the largest box of the page's aspect that fits the screen; width mode = full width
  const availH = full ? "100vh" : `(100vh - ${reserve}px)`;
  const frameWidth = fitMode === "width" ? "100%" : `min(100%, calc(${availH} * ${aspect}))`;

  const outer: CSSProperties = full
    ? { position: pseudo ? "fixed" : undefined, inset: pseudo ? 0 : undefined, zIndex: pseudo ? 9000 : undefined, width: "100vw", height: "100vh", background: PREVIEW_STAGE_BG[theme], display: "flex", alignItems: fitMode === "width" ? "flex-start" : "center", justifyContent: "center", overflow: fitMode === "width" ? "auto" : "hidden" }
    : { position: "relative", display: "flex", justifyContent: "center" };

  return (
    <div ref={wrap} className="st-tvv" style={outer} onMouseMove={reveal} data-full={full ? "1" : undefined}>
      <div style={{ width: frameWidth, flex: "0 0 auto" }}>
        <TvFrame
          ref={frame}
          reportId={reportId}
          source={source}
          shareToken={shareToken}
          theme={theme}
          fit="fixed"
          adaptive
          rounded={!full}
          reloadKey={reloadKey}
          onLoaded={onLoaded}
          onPage={onPage}
          onLayout={(l: TvFrameLayout) => setAspect(l.width / l.height)}
          onPointer={reveal}
        />
      </div>
      <div className={`st-tvv__bar${barVisible ? "" : " is-hidden"}${full ? " is-full" : ""}`} onMouseEnter={reveal}>
        {full && navPages.length > 1 ? (
          <>
            <button type="button" className="st-tvv__btn" aria-label="Previous page" disabled={idx <= 0} onClick={() => go(idx - 1)}><ChevronLeft size={16} /></button>
            <span className="st-tvv__page">{navPages[Math.max(0, idx)]?.name}</span>
            <button type="button" className="st-tvv__btn" aria-label="Next page" disabled={idx < 0 || idx >= navPages.length - 1} onClick={() => go(idx + 1)}><ChevronRight size={16} /></button>
          </>
        ) : null}
        <FitModeToggle value={fitMode} onChange={onFitMode} />
        <PreviewThemeToggle compact theme={theme} onChange={onThemeChange} />
        <button type="button" className="st-tvv__btn" aria-label={full ? "Exit full screen (Esc)" : "Full screen"} title={full ? "Exit full screen (Esc)" : "Full screen"} onClick={toggleFullscreen}>
          {full ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
      </div>
    </div>
  );
});
